import type { OnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';

/** A status the member cannot act on any further — the step has had its answer. */
const RESOLVED_STATUSES = new Set(['completed', 'skipped']);

/**
 * Whether the member has yet to touch the Auto Statements step. Once they have enabled a bank
 * (`in_progress`) or finished the step (`completed`), resolvability decides nothing about it: a bank
 * withdrawn since then reads not-resolvable, yet the member still owes Continue, or is already done. So
 * only an untouched step may be hidden or skipped for them, here and in ConnectStatements.
 */
export function isConnectStatementsUntouched(onboardingProgress: OnboardingProgress | undefined): boolean {
    return onboardingProgress?.connect_automated_statements_status === 'not_started';
}

/**
 * Whether the legacy wizard may drop the step unvisited and record the skip itself: an untouched step
 * with nothing eligible (`eligibility` is `false`: every confirmed business institution has a settled
 * not-resolvable result). The wizard's hide rule and the skip both use it, so a hidden step always gets
 * skipped rather than left with no way back to it.
 */
export function isAutoSkippable(
    onboardingProgress: OnboardingProgress | undefined,
    eligibility: false | undefined
): boolean {
    return eligibility === false && isConnectStatementsUntouched(onboardingProgress);
}

/**
 * Whether the Auto Statements wizard step should be offered at all — the legacy, gate-off rule.
 *
 * False once the member has skipped it, or when `isAutoSkippable` says the wizard skips it for them.
 * `undefined` eligibility means undetermined or resolvable, so the step is offered and its own fallback
 * decides on arrival.
 *
 * The V1 redesign uses `isConnectStatementsResolved` instead; this is kept for the accounting
 * wizard, which still owns the step while the gate is off.
 */
export default function shouldShowConnectStatementsWizardStep(
    onboardingProgress: OnboardingProgress | undefined,
    eligibility: false | undefined
): boolean {
    return (
        onboardingProgress?.connect_automated_statements_status !== 'skipped' &&
        !isAutoSkippable(onboardingProgress, eligibility)
    );
}

/**
 * Whether the member has already settled Auto Statements, either way.
 *
 * Under the V1 redesign the step is mandatory: business accounts setup does not complete until this
 * is true, so resolving it is the only thing that takes the step out of that wizard. Syncing
 * statements is what lets book-building start without waiting on monthly uploads, and the step is
 * the last thing between the member and a completed stage — so it is deliberately friction rather
 * than an option they can leave open.
 *
 * Eligibility deliberately plays no part, unlike the legacy rule above. A member with no eligible
 * institution still walks into the step; ConnectStatements settles it on arrival by recording the
 * skip and advancing. Pre-judging it here only worked when the resolvability cache happened to be
 * warm, which on a first walk it is not — the cache is filled by the labelling step (connect-account
 * under V1, classify-accounts before it), well after this wizard mounts and takes its decision.
 */
export function isConnectStatementsResolved(onboardingProgress: OnboardingProgress | undefined): boolean {
    return RESOLVED_STATUSES.has(onboardingProgress?.connect_automated_statements_status ?? '');
}

/**
 * Whether the member has already settled manual statement upload, either way.
 *
 * Read on its own rather than inferred from Auto Statements: syncing settles that step before the
 * member ever reaches the upload, so the two resolve at different times and for different reasons.
 */
export function isStatementUploadResolved(onboardingProgress: OnboardingProgress | undefined): boolean {
    return RESOLVED_STATUSES.has(onboardingProgress?.statement_upload_status ?? '');
}
